import { mapActions, mapGetters } from 'vuex';
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import assistantApi from '@/services/apiService/assistant';
import customColorMixin from '@/mixins/customColor';
import { getContrastYIQ } from '@/utils/themes';
const DEFAULT_ACCENT_COLOR = '#1d4ed8';
const md = new MarkdownIt({
  html: false,
  breaks: true,
  linkify: false
}).disable(['heading', 'hr', 'image', 'table', 'fence', 'code']);
const TASK_STATUS = {
  IN_PROGRESS: 'inProgress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  FAILED: 'failed'
};
const TASK_TYPE_LABEL_KEY = {
  createVirtualDevice: 'Assistant.taskTypes.createVirtualDevice',
  createDashboard: 'Assistant.taskTypes.createDashboard',
  addWidget: 'Assistant.taskTypes.addWidget'
};

// Explicit allowlist of route.query keys forwarded to the assistant. Anything
// not in this list is dropped to avoid leaking unintended values (tokens, PII,
// emails) into the LLM prompt and the provider's prompt cache. Add a key here
// only after confirming the value is non-sensitive and useful for reasoning.
const SAFE_ROUTE_QUERY_KEYS = ['edit', 'tab', 'mode', 'view'];

// Short affirmative replies that should confirm a pending action when one is
// visible, instead of being treated as a brand new chat turn. We accept only
// unambiguous tokens — never substrings — to avoid auto-confirming when the
// user is mid-sentence (e.g. "yes please change the connector to ...").
const AFFIRMATIVE_CONFIRMATIONS = new Set([
// English
'yes', 'y', 'yeah', 'yep', 'yup', 'ok', 'okay', 'sure', 'go', 'go ahead', 'do it', 'proceed', 'confirm', 'submit', 'create it', 'please do',
// German
'ja', 'jo', 'klar', 'mach', 'mach es', 'los', 'los gehts', "los geht's", 'bestätige', 'bestätigen', 'bestaetigen', 'erstelle', 'erstellen']);
function isAffirmativeConfirmation(message) {
  if (!message) {
    return false;
  }
  return AFFIRMATIVE_CONFIRMATIONS.has(message.trim().toLowerCase().replace(/[.!]+$/, ''));
}
export default {
  name: 'CoreAssistantChat',
  mixins: [customColorMixin],
  props: {
    dataCy: {
      type: String,
      default: 'assistant_chat'
    }
  },
  data() {
    return {
      draft: '',
      sending: false,
      confirming: false,
      modelProfiles: [],
      selectedModelProfileId: null,
      pendingAction: null,
      messages: [],
      tasks: [],
      tasksLoadedFor: null
    };
  },
  computed: {
    ...mapGetters('authentication', ['tenantId', 'user']),
    ...mapGetters('app', {
      isOpen: 'assistantOpen',
      tasksCollapsed: 'assistantTasksCollapsed'
    }),
    accentColor() {
      // customColor comes from customColorMixin and tracks the active app color
      // (vuetifyColors[app.color] or the custom branding hex).
      return this.customColor || DEFAULT_ACCENT_COLOR;
    },
    accentTextColor() {
      return getContrastYIQ(this.accentColor, '#1f2330');
    },
    accentStyle() {
      return {
        '--assistant-accent': this.accentColor,
        '--assistant-accent-text': this.accentTextColor
      };
    },
    canSend() {
      return !!this.draft.trim() && !this.sending && !this.confirming && !!this.selectedModelProfileId;
    },
    pendingActionPreviewEntries() {
      if (!this.pendingAction || !this.pendingAction.preview) {
        return [];
      }
      const labels = {
        title: this.$i18n_t('Common.title'),
        tenantId: this.$i18n_t('Assistant.tenant'),
        bridgeConfigId: 'Bridge Config ID',
        externalDeviceId: 'External Device ID',
        name: this.$i18n_t('Assistant.previewLabels.name'),
        parentDtwinId: this.$i18n_t('Assistant.previewLabels.parentDtwinId'),
        tags: this.$i18n_t('Assistant.previewLabels.tags'),
        columns: this.$i18n_t('Assistant.previewLabels.columns')
      };
      return Object.keys(this.pendingAction.preview).filter(key => this.pendingAction.preview[key] !== undefined && this.pendingAction.preview[key] !== null).filter(key => labels[key]).map(key => ({
        key,
        label: labels[key],
        value: this.pendingAction.preview[key]
      }));
    }
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(value) {
        if (value) {
          document.addEventListener('keydown', this.onKeydown);
          this.$nextTick(() => this.handleOpen());
        } else {
          document.removeEventListener('keydown', this.onKeydown);
        }
      }
    }
  },
  created() {
    this.resetMessages();
    this.hydrateAssistantTasksCollapsed();
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    ...mapActions('app', ['closeAssistant', 'toggleAssistantTasks', 'hydrateAssistantTasksCollapsed']),
    renderMarkdown(text) {
      if (!text) {
        return '';
      }
      return DOMPurify.sanitize(md.render(String(text)));
    },
    closeChat() {
      this.closeAssistant();
    },
    toggleTasks() {
      this.toggleAssistantTasks();
    },
    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeChat();
      }
    },
    async handleOpen() {
      if (this.tasksLoadedFor !== this.tenantId) {
        this.resetMessages();
        this.pendingAction = null;
        this.tasks = [];
        this.tasksLoadedFor = this.tenantId;
      }
      if (!this.modelProfiles.length) {
        await this.loadModelProfiles();
      }
      this.scrollMessagesToBottom();
      this.focusComposer();
    },
    resetMessages() {
      this.messages = [{
        role: 'assistant',
        content: this.$i18n_t('Assistant.welcome')
      }];
    },
    normalizeMessagesForRequest() {
      return this.messages.filter(message => message.content).map(message => ({
        role: message.role,
        content: message.content
      }));
    },
    buildClientContext() {
      const context = {
        language: this.$i18n.locale
      };
      const route = this.$route;
      if (route) {
        const routePayload = {};
        if (route.name) {
          routePayload.name = route.name;
        }
        if (route.path) {
          // route.path excludes the query string; route.fullPath includes it
          // and is intentionally NOT forwarded — query strings can carry
          // tokens, emails, or other PII into the LLM prompt.
          routePayload.path = route.path;
        }
        if (route.params && Object.keys(route.params).length) {
          routePayload.params = {
            ...route.params
          };
        }
        // Only forward an explicit allowlist of query keys we know are safe
        // and useful for the assistant's reasoning. Anything else (including
        // unknown future keys) is dropped.
        if (route.query) {
          const filteredQuery = SAFE_ROUTE_QUERY_KEYS.reduce((acc, key) => {
            const value = route.query[key];
            if (value !== undefined && value !== null && value !== '') {
              acc[key] = value;
            }
            return acc;
          }, {});
          if (Object.keys(filteredQuery).length) {
            routePayload.query = filteredQuery;
          }
        }
        if (Object.keys(routePayload).length) {
          context.route = routePayload;
        }
      }
      return context;
    },
    appendMessage(role, content = '') {
      this.messages.push({
        role,
        content
      });
      return this.messages.length - 1;
    },
    appendAssistantMessage(content) {
      if (!content) {
        return undefined;
      }
      return this.appendMessage('assistant', content);
    },
    updateMessageContent(index, content) {
      if (index < 0 || !this.messages[index]) {
        return;
      }
      this.$set(this.messages, index, {
        ...this.messages[index],
        content
      });
    },
    async loadModelProfiles() {
      try {
        var _this$modelProfiles$;
        const response = await assistantApi.fetchModelProfiles();
        this.modelProfiles = response.profiles || [];
        this.selectedModelProfileId = response.defaultModelProfileId || ((_this$modelProfiles$ = this.modelProfiles[0]) === null || _this$modelProfiles$ === void 0 ? void 0 : _this$modelProfiles$.id) || null;
      } catch (error) {
        this.appendAssistantMessage(this.$i18n_t('Assistant.loadProfilesFailed'));
        this.scrollMessagesToBottom();
      }
    },
    resolveAssistantErrorMessage(responseMessage, responseData, fallbackKey) {
      if (responseMessage && this.$i18n_te(`Error.${responseMessage}`)) {
        return this.$i18n_t(`Error.${responseMessage}`, responseData || {});
      }
      return this.$i18n_t(fallbackKey);
    },
    formatAssistantError(error, fallbackKey) {
      var _error$response, _error$response2;
      const responseMessage = error === null || error === void 0 || (_error$response = error.response) === null || _error$response === void 0 || (_error$response = _error$response.data) === null || _error$response === void 0 ? void 0 : _error$response.message;
      const responseData = error === null || error === void 0 || (_error$response2 = error.response) === null || _error$response2 === void 0 || (_error$response2 = _error$response2.data) === null || _error$response2 === void 0 ? void 0 : _error$response2.data;
      return this.resolveAssistantErrorMessage(responseMessage, responseData, fallbackKey);
    },
    async streamAssistantResponse(payload, {
      fallbackKey,
      onSuccess
    } = {}) {
      const assistantMessageIndex = this.appendMessage('assistant', '');
      let streamedText = '';
      try {
        await assistantApi.chatStream(payload, {
          onEvent: async event => {
            switch (event.type) {
              case 'message_delta':
                streamedText += event.delta || '';
                this.updateMessageContent(assistantMessageIndex, streamedText);
                this.scrollMessagesToBottom();
                break;
              case 'pending_action':
                this.pendingAction = event.pendingAction || null;
                this.upsertTaskFromPendingAction(this.pendingAction);
                if (!streamedText) {
                  this.updateMessageContent(assistantMessageIndex, this.$i18n_t('Assistant.actionReady'));
                }
                this.scrollMessagesToBottom();
                break;
              case 'action_result':
                this.pendingAction = null;
                this.markTaskCompleted(event.actionResult);
                this.handleActionNavigation(event.actionResult);
                if (typeof onSuccess === 'function') {
                  onSuccess(event.actionResult);
                }
                this.queueFollowUp(event.actionResult);
                break;
              case 'action_failed':
                this.pendingAction = null;
                this.markTaskFailed(event.actionResult);
                break;
              case 'error':
                this.updateMessageContent(assistantMessageIndex, this.resolveAssistantErrorMessage(event.message, event.data, fallbackKey));
                this.scrollMessagesToBottom();
                break;
              case 'done':
                if (!streamedText && !this.messages[assistantMessageIndex].content) {
                  this.updateMessageContent(assistantMessageIndex, this.$i18n_t(fallbackKey));
                }
                this.scrollMessagesToBottom();
                break;
              default:
                break;
            }
          }
        });
      } catch (error) {
        this.updateMessageContent(assistantMessageIndex, this.formatAssistantError(error, fallbackKey));
        this.scrollMessagesToBottom();
      }
    },
    async sendMessage() {
      const message = this.draft.trim();
      if (!message || !this.selectedModelProfileId) {
        return;
      }
      if (this.pendingAction && isAffirmativeConfirmation(message)) {
        this.draft = '';
        await this.confirmPendingAction({
          userMessage: message
        });
        return;
      }
      this.appendMessage('user', message);
      this.draft = '';
      this.sending = true;
      this.scrollMessagesToBottom();
      try {
        await this.streamAssistantResponse({
          selectedModelProfileId: this.selectedModelProfileId,
          clientContext: this.buildClientContext(),
          messages: this.normalizeMessagesForRequest()
        }, {
          fallbackKey: 'Assistant.chatFailedMessage'
        });
      } finally {
        this.sending = false;
        this.focusComposer();
      }
    },
    async confirmPendingAction({
      userMessage
    } = {}) {
      if (!this.pendingAction || !this.selectedModelProfileId) {
        return;
      }
      const action = this.pendingAction;
      const messageText = userMessage || this.$i18n_t('Assistant.confirmIntent');
      this.appendMessage('user', messageText);
      this.confirming = true;
      this.scrollMessagesToBottom();
      try {
        await this.streamAssistantResponse({
          selectedModelProfileId: this.selectedModelProfileId,
          clientContext: this.buildClientContext(),
          messages: this.normalizeMessagesForRequest(),
          confirmation: {
            decision: 'confirm',
            action
          }
        }, {
          fallbackKey: 'Assistant.confirmFailedMessage',
          onSuccess: () => {
            this.pendingAction = null;
          }
        });
      } finally {
        this.confirming = false;
        this.focusComposer();
      }
    },
    dismissPendingAction() {
      const action = this.pendingAction;
      this.pendingAction = null;
      if (action) {
        this.markTaskCancelled(action);
      }
    },
    upsertTaskFromPendingAction(pendingAction) {
      if (!pendingAction) {
        return;
      }
      const existingIndex = this.tasks.findIndex(task => task.pendingId === this.pendingActionKey(pendingAction));
      const labelKey = TASK_TYPE_LABEL_KEY[pendingAction.actionType];
      const baseName = labelKey ? this.$i18n_t(labelKey) : pendingAction.actionType;
      const title = pendingAction.preview && pendingAction.preview.title;
      const taskName = title ? `${baseName}: ${title}` : baseName;
      const next = {
        id: existingIndex >= 0 ? this.tasks[existingIndex].id : `task-${Date.now()}`,
        pendingId: this.pendingActionKey(pendingAction),
        actionType: pendingAction.actionType,
        name: taskName,
        status: TASK_STATUS.IN_PROGRESS
      };
      if (existingIndex >= 0) {
        this.$set(this.tasks, existingIndex, {
          ...this.tasks[existingIndex],
          ...next
        });
      } else {
        this.tasks.unshift(next);
      }
    },
    queueFollowUp(actionResult) {
      const prompt = actionResult && actionResult.followUpUserPrompt;
      if (!prompt || typeof prompt !== 'string' || !prompt.trim()) {
        return;
      }
      // Defer: let the current stream finish, navigation settle, and the action_result
      // reply land in the chat first. Then fire the follow-up as a fresh user turn.
      setTimeout(() => {
        this.sendFollowUpMessage(prompt.trim());
      }, 600);
    },
    async sendFollowUpMessage(message) {
      if (!message || !this.selectedModelProfileId) {
        return;
      }
      this.appendMessage('user', message);
      this.sending = true;
      this.scrollMessagesToBottom();
      try {
        await this.streamAssistantResponse({
          selectedModelProfileId: this.selectedModelProfileId,
          clientContext: this.buildClientContext(),
          messages: this.normalizeMessagesForRequest()
        }, {
          fallbackKey: 'Assistant.chatFailedMessage'
        });
      } finally {
        this.sending = false;
        this.focusComposer();
      }
    },
    handleActionNavigation(actionResult) {
      const navigation = actionResult && actionResult.navigation;
      if (!navigation || !navigation.name) {
        return;
      }
      const target = {
        name: navigation.name,
        params: navigation.params || {}
      };
      const current = this.$route;
      // Avoid NavigationDuplicated when we're already on the same page.
      if (current && current.name === target.name && JSON.stringify(current.params) === JSON.stringify(target.params)) {
        return;
      }
      this.$router.push(target).catch(() => {
        // navigation can be aborted by other guards; swallow silently.
      });
    },
    markTaskCompleted(actionResult) {
      if (!actionResult) {
        return;
      }
      const index = this.tasks.findIndex(task => task.actionType === actionResult.actionType && task.status === TASK_STATUS.IN_PROGRESS);
      if (index >= 0) {
        this.$set(this.tasks, index, {
          ...this.tasks[index],
          status: TASK_STATUS.COMPLETED
        });
      }
    },
    markTaskCancelled(pendingAction) {
      const key = this.pendingActionKey(pendingAction);
      const index = this.tasks.findIndex(task => task.pendingId === key && task.status === TASK_STATUS.IN_PROGRESS);
      if (index >= 0) {
        this.$set(this.tasks, index, {
          ...this.tasks[index],
          status: TASK_STATUS.CANCELLED
        });
      }
    },
    markTaskFailed(actionResult) {
      if (!actionResult) {
        return;
      }
      const index = this.tasks.findIndex(task => task.actionType === actionResult.actionType && task.status === TASK_STATUS.IN_PROGRESS);
      if (index >= 0) {
        this.$set(this.tasks, index, {
          ...this.tasks[index],
          status: TASK_STATUS.FAILED
        });
      }
    },
    pendingActionKey(pendingAction) {
      if (!pendingAction) {
        return '';
      }
      return `${pendingAction.actionType}:${JSON.stringify(pendingAction.preview || {})}`;
    },
    taskIcon(status) {
      switch (status) {
        case TASK_STATUS.COMPLETED:
          return 'fal fa-check-circle';
        case TASK_STATUS.CANCELLED:
          return 'fal fa-times-circle';
        case TASK_STATUS.FAILED:
          return 'fal fa-exclamation-circle';
        default:
          return 'fal fa-circle-notch fa-spin';
      }
    },
    taskColor(status) {
      switch (status) {
        case TASK_STATUS.COMPLETED:
          return 'success';
        case TASK_STATUS.CANCELLED:
          return 'grey';
        case TASK_STATUS.FAILED:
          return 'danger';
        default:
          return 'primary';
      }
    },
    scrollMessagesToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messages) {
          this.$refs.messages.scrollTop = this.$refs.messages.scrollHeight;
        }
      });
    },
    focusComposer() {
      // After a streamed response finishes the textarea was disabled for the
      // duration of the request; re-enabling alone doesn't return focus, so
      // the user has to click into it before they can type again. Wait one
      // tick so the disabled attribute is removed first, then focus.
      this.$nextTick(() => {
        const composer = this.$refs.composer;
        if (composer && typeof composer.focus === 'function') {
          composer.focus();
        }
      });
    }
  }
};